import React, {startTransition, useEffect, useRef, useState} from "react"
import {FavouriteState} from "../../models/Favourites"
import {FavouritesIcon, FavouritesButton} from "./components"
import FavouritesContainer from "./FavouritesContainer"
import connect from "./connect"
import {useProductFavourites} from "../../hooks/useProductFavourites"
import {
    getFavouriteIconPath,
    shoppingListItemsToFavouritedColourways,
    setFavoritesCallback,
    getNextFavouritesData,
} from "../../utils/favourites"
import FavouritesErrorToolTip from "../favouritesErrorToolTip"
import FavouritesToolTip from "../favouritesToolTip"
import {getFavourtiesSubscriptionCallBack} from "../../events"
import {TextModel} from "../../models/Text"

interface ProductFavouritesProps {
    addToFavourites: () => void
    removeFromFavourites: () => void
    baseUrl: string
    isFav: boolean
    itemNumber: string
    isLoadingFav: boolean
    text: TextModel
    setFavouritedColourways: (favoritedColourways: string[]) => void
    animateFavouriteIcon: boolean
    favouriteButtonTitleAndLabel: string
    realm: string
    noPadding: boolean | null
}

export const ProductFavourites = ({
    isFav,
    addToFavourites,
    removeFromFavourites,
    baseUrl,
    setFavouritedColourways,
    isLoadingFav,
    text,
    animateFavouriteIcon,
    favouriteButtonTitleAndLabel,
    itemNumber,
    realm,
    noPadding,
}: ProductFavouritesProps) => {
    const [favClicked, setFavClicked] = useState(false)

    const {toggleProductFavourites, favState} = useProductFavourites({
        isFav,
        addToFavourites,
        removeFromFavourites,
        isLoadingFav,
        setFavClicked,
    })

    useEffect(() => {
        startTransition(() => {
            const favouritesData = getNextFavouritesData()
            if (favouritesData) {
                setFavouritedColourways(shoppingListItemsToFavouritedColourways(favouritesData?.ShoppingListItems))
            } else {
                getFavourtiesSubscriptionCallBack(setFavoritesCallback, setFavouritedColourways)
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const favoritesIconRef = useRef(null)

    const getTranslatedFavState = (favouriteState: FavouriteState) => {
        const {inactive, loading, active} = text.favouriteStates
        switch (favouriteState) {
            case FavouriteState.Active:
                return active
            case FavouriteState.Inactive:
                return inactive
            case FavouriteState.Loading:
                return loading
            default:
                return ""
        }
    }

    const formatButtonTitleAndLabel = `${favouriteButtonTitleAndLabel} ${getTranslatedFavState(favState)}`

    return (
        <FavouritesContainer suppressHydrationWarning>
            <FavouritesButton
                $noPadding={noPadding}
                onClick={toggleProductFavourites}
                data-testid="product-summary-favourites-button"
                aria-label={formatButtonTitleAndLabel}
                suppressHydrationWarning
                // title={formatButtonTitleAndLabel}
            >
                <FavouritesIcon
                    suppressHydrationWarning
                    id={`product-summary-favourites-icon-${itemNumber}`}
                    data-testid={`product-${favState}-favourite-icon`}
                    $favState={favState}
                    ref={favoritesIconRef}
                    $shouldAnimate={animateFavouriteIcon}
                    $gifPath={getFavouriteIconPath("favourites-loading.gif", null, realm)}
                    $activePNGPath={getFavouriteIconPath("favourites-active.png", null, realm)}
                    $inactivePNGPath={getFavouriteIconPath("favourites-inactive.png", null, realm)}
                >
                    <title>{formatButtonTitleAndLabel}</title>
                    <use
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                        xlinkHref={getFavouriteIconPath("favourites.svg", favState, realm)}
                        // eslint-disable-next-line react/no-unknown-property
                        viewBox="0 0 26 24"
                    />
                </FavouritesIcon>
            </FavouritesButton>
            <FavouritesToolTip
                isProductFavourited={isFav && favClicked}
                favState={favState}
                baseUrl={baseUrl}
                referenceElement={favoritesIconRef.current}
                text={text}
                setFavClicked={setFavClicked}
            />
            <FavouritesErrorToolTip referenceElement={favoritesIconRef.current} />
        </FavouritesContainer>
    )
}

export default connect(ProductFavourites)
